import { useEffect, useState } from "react";
import { api, fmtINR, fmtDate, API_BASE } from "@/lib/api";
import { Link } from "react-router-dom";
import { Search, Download, Send, FileText } from "lucide-react";

export default function ReceiptRegister() {
  const [rows, setRows] = useState([]);
  const [branches, setBranches] = useState([]);
  const [q, setQ] = useState("");
  const [branch, setBranch] = useState("");
  const [mode, setMode] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (branch) params.branch_code = branch;
    if (mode) params.mode = mode;
    if (from) params.from_date = from;
    if (to) params.to_date = to;
    const { data } = await api.get("/receipts", { params });
    setRows(data);
    setLoading(false);
  }

  useEffect(() => {
    api.get("/meta/branches").then((r) => setBranches(r.data));
    load();
    // eslint-disable-next-line
  }, []);

  function csv() {
    const header = ["Receipt No", "Date", "Branch", "Customer", "Mobile", "Vehicle", "Total", "Modes"];
    const lines = rows.map((r) => [
      r.receipt_no, r.created_at?.slice(0, 10), r.branch_name, r.customer_name, r.mobile,
      r.vehicle_model || "", r.total_amount, r.payments.map((p) => `${p.mode}:${p.amount}`).join("|")
    ].map((x) => `"${String(x).replaceAll('"', '""')}"`).join(","));
    const blob = new Blob([[header.join(",")].concat(lines).join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `receipts_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const totalSum = rows.reduce((s, r) => s + r.total_amount, 0);

  return (
    <div className="space-y-6" data-testid="receipt-register-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900">Receipt Register</h1>
          <p className="text-slate-500 mt-1">{rows.length} receipts · Total {fmtINR(totalSum)}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={csv} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50">
            <Download className="h-4 w-4" /> Export CSV
          </button>
          <Link to="/new-receipt" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700">
            <FileText className="h-4 w-4" /> New Receipt
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-slate-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <div className="md:col-span-2 relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="filter-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by receipt no, name, mobile, chassis…"
              className="w-full pl-9 h-10 rounded-xl border border-slate-200 outline-none focus:border-blue-500 text-sm"
            />
          </div>
          <select data-testid="filter-branch" value={branch} onChange={(e) => setBranch(e.target.value)} className="h-10 rounded-xl border border-slate-200 px-2 text-sm">
            <option value="">All branches</option>
            {branches.map((b) => <option key={b.code} value={b.code}>{b.name}</option>)}
          </select>
          <select data-testid="filter-mode" value={mode} onChange={(e) => setMode(e.target.value)} className="h-10 rounded-xl border border-slate-200 px-2 text-sm">
            <option value="">All modes</option>
            {["CASH", "UPI", "NEFT", "RTGS", "CHEQUE"].map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-10 rounded-xl border border-slate-200 px-2 text-sm" />
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-10 rounded-xl border border-slate-200 px-2 text-sm" />
          <div className="md:col-span-6 flex justify-end">
            <button onClick={load} data-testid="apply-filters" className="px-5 h-10 rounded-xl bg-slate-900 text-white text-sm font-semibold">Apply</button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs uppercase text-slate-500 tracking-wider">
                <th className="py-3 px-4">Receipt No</th>
                <th className="py-3 px-4">Date</th>
                <th className="py-3 px-4">Branch</th>
                <th className="py-3 px-4">Customer</th>
                <th className="py-3 px-4">Modes</th>
                <th className="py-3 px-4 text-right">Total</th>
                <th className="py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="receipts-tbody">
              {loading && <tr><td colSpan={7} className="py-8 text-center text-slate-400">Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-slate-400">No receipts found</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`receipt-row-${r.receipt_no}`}>
                  <td className="py-3 px-4 mono font-bold text-slate-800">{r.receipt_no}</td>
                  <td className="py-3 px-4 text-slate-600">{fmtDate(r.created_at)}</td>
                  <td className="py-3 px-4">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 text-xs font-semibold">{r.branch_name}</span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="font-semibold text-slate-800">{r.customer_name}</div>
                    <div className="text-xs text-slate-400">{r.mobile}</div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex flex-wrap gap-1">
                      {r.payments.map((p, i) => (
                        <span key={`${r.id}-${p.mode}-${i}`} className={`text-[10px] px-2 py-0.5 rounded-md font-semibold ${p.mode === "CASH" ? "bg-emerald-50 text-emerald-700" : "bg-indigo-50 text-indigo-700"}`}>
                          {p.mode} {fmtINR(p.amount).replace(" ", "")}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="py-3 px-4 text-right mono font-bold text-slate-800">{fmtINR(r.total_amount)}</td>
                  <td className="py-3 px-4 text-right space-x-2">
                    <a href={`${API_BASE}/receipts/${r.id}/pdf`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700">
                      <Download className="h-3 w-3" /> PDF
                    </a>
                    <a
                      href={`https://wa.me/${(r.mobile.replace(/\D/g, "").length === 10 ? "91" : "") + r.mobile.replace(/\D/g, "")}?text=${encodeURIComponent(`Dear ${r.customer_name}, receipt ${r.receipt_no} — ₹${r.total_amount}. Download: ${API_BASE}/receipts/${r.id}/pdf — Ratan Motors`)}`}
                      target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700"
                    >
                      <Send className="h-3 w-3" /> WhatsApp
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
